import { memo, useMemo } from 'react'
import { Button, Box, Avatar, Chip } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'

// Unified Practitioner Button component for both main header and floating headers
// Takes primitive props directly to avoid complex memoization issues
const SimplePractitionerButton = memo(({ 
  imageUrl, 
  managerId,
  buttonText, 
  onClick,
  isActive = false,
  variant = 'default', // 'default' or 'floating' for more prominent styling
  role = null // 'Doctor' or 'Therapist' to display role
}) => {
  // Create avatar directly here with stable props
  // Use a stable key based on managerId to prevent remounting when imageUrl changes but managerId is same
  const avatar = useMemo(() => (
    <Avatar
      key={`practitioner-avatar-${managerId || 'none'}`} // Stable key prevents remounting
      src={imageUrl || undefined}
      sx={{
        width: 32,
        height: 32,
        // Prominent styling for floating variant
        ...(variant === 'floating' ? {
          bgcolor: isActive ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
          color: '#ffffff',
          border: '2px solid rgba(255, 255, 255, 0.5)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        } : {
          bgcolor: 'rgba(116, 80, 170, 0.1)',
          color: 'rgba(116, 80, 170, 0.9)',
          border: '2px solid rgba(116, 80, 170, 0.2)',
        }),
        flexShrink: 0,
      }}
      imgProps={{
        style: {
          objectFit: 'cover',
          width: '100%',
          height: '100%',
        },
        onError: (e) => {
          e.target.onerror = null
          e.target.src = ''
        },
        loading: 'lazy',
        decoding: 'async',
      }}
    >
      <PersonIcon sx={{ fontSize: 18 }} />
    </Avatar>
  ), [imageUrl, managerId, variant, isActive]) // Include variant and isActive in dependencies

  return (
    <Button
      variant="outlined"
      disabled={!onClick}
      startIcon={
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          {avatar}
          {isActive && (
            <Chip
              label="✓"
              size="small"
              sx={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                height: '18px',
                minWidth: '18px',
                fontSize: '10px',
                fontWeight: 700,
                backgroundColor: '#10b981',
                color: 'white',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                zIndex: 1,
                '& .MuiChip-label': {
                  padding: '0 2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              }}
            />
          )}
        </Box>
      }
      onClick={onClick || undefined}
      sx={{
        minWidth: { xs: '120px', sm: '140px', md: '160px' },
        maxWidth: { xs: '140px', sm: '180px', md: '220px' },
        justifyContent: 'flex-start',
        textTransform: 'none',
        // Prominent styling for floating header variant
        ...(variant === 'floating' ? {
          borderColor: isActive 
            ? '#10b981' 
            : '#6779e3',
          borderWidth: '2px',
          color: '#ffffff',
          backgroundColor: isActive 
            ? 'rgba(16, 185, 129, 0.9)' 
            : '#6779e3',
          fontWeight: 600,
          boxShadow: isActive 
            ? '0 2px 8px rgba(16, 185, 129, 0.4)' 
            : '0 2px 8px rgba(103, 121, 227, 0.3)',
          '&:hover': {
            borderColor: isActive 
              ? '#10b981' 
              : '#5568d3',
            backgroundColor: isActive 
              ? 'rgba(16, 185, 129, 1)' 
              : '#5568d3',
            boxShadow: isActive 
              ? '0 4px 12px rgba(16, 185, 129, 0.5)' 
              : '0 4px 12px rgba(103, 121, 227, 0.4)',
            transform: 'translateY(-1px)',
          },
        } : {
          // Default styling for main header
          borderColor: isActive 
            ? 'rgba(16, 185, 129, 0.5)' 
            : 'rgba(116, 80, 170, 0.3)',
          color: 'rgba(116, 80, 170, 0.9)',
          backgroundColor: isActive 
            ? 'rgba(16, 185, 129, 0.05)' 
            : 'transparent',
          '&:hover': {
            borderColor: isActive 
              ? 'rgba(16, 185, 129, 0.7)' 
              : 'rgba(116, 80, 170, 0.5)',
            backgroundColor: isActive 
              ? 'rgba(16, 185, 129, 0.08)' 
              : 'rgba(116, 80, 170, 0.05)',
          },
        }),
        padding: { xs: '6px 8px', sm: '7px 10px', md: '8px 12px' },
        fontSize: { xs: '11px', sm: '12px', md: '13px' },
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'all 0.2s ease-in-out',
        '& .MuiButton-startIcon': {
          marginRight: '8px',
          flexShrink: 0,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', minWidth: 0 }}>
        <Box component="span" sx={{ fontSize: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {buttonText}
        </Box>
        {role && (
          <Chip
            label={role}
            size="small"
            sx={{
              height: '18px',
              fontSize: '9px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              padding: '0 4px',
              minWidth: 'auto',
              backgroundColor: variant === 'floating' 
                ? 'rgba(255, 255, 255, 0.25)' 
                : 'rgba(116, 80, 170, 0.15)',
              color: variant === 'floating' 
                ? '#ffffff' 
                : 'rgba(116, 80, 170, 0.9)',
              border: variant === 'floating'
                ? '1px solid rgba(255, 255, 255, 0.4)'
                : '1px solid rgba(116, 80, 170, 0.3)',
              flexShrink: 0,
              '& .MuiChip-label': {
                padding: '0 4px',
                lineHeight: 1.2,
              },
            }}
          />
        )}
      </Box>
    </Button>
  )
}, (prevProps, nextProps) => {
  // Simple comparison of primitive values
  return (
    prevProps.imageUrl === nextProps.imageUrl &&
    prevProps.managerId === nextProps.managerId &&
    prevProps.buttonText === nextProps.buttonText &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.variant === nextProps.variant &&
    prevProps.role === nextProps.role
  )
})

SimplePractitionerButton.displayName = 'SimplePractitionerButton'

export default SimplePractitionerButton

